/*
 * ---------------------------------------------------------------------------------
 * Copyright:
 *      NewtonGreen Technologies Pty. Ltd.
 *      Level 4, 175 Scott St.
 *      Newcastle, NSW, 2300
 *      Australia
 *
 *      E-mail: support@newtongreen.com
 *      Tel: (02) 4925 5288
 *      Fax: (02) 4925 3068
 *
 *      All Rights Reserved.
 * ---------------------------------------------------------------------------------
 */

/*
 * --------------------------------------------------------------------------------
 * This file contains the theme for the application
 * --------------------------------------------------------------------------------
 */

/*
 * ---------------------------------------------------------------------------------
 * Imports - External
 * ---------------------------------------------------------------------------------
 */

/**
 * Required to make use of JSX functionality
 */
import * as React from 'react';

import { Redirect, Switch } from 'react-router';

/**
 * Used to normalise CSS between browsers.
 */
import CssBaseline from '@material-ui/core/CssBaseline';

/**
 * Used to create a contianer for the page.
 */
import Container from '@material-ui/core/Container';

/**
 * Used to create a contianer for the page.
 */
import Paper from '@material-ui/core/Paper';

/**
 * Used to create a style hook to utilise JSS
 */
import { Theme, makeStyles } from '@material-ui/core/styles';

import { FormByContextRoute, InstitutionByCodeRoute, InstitutionsRoute, PatientByStudyNumberRoute, Route, RouteLoading, onlinePatientManagement, useAuthenticatedUser } from '@ngt/online-patient-management-ui';


import Typography from '@material-ui/core/Typography';

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

import { faCopyright } from '@fortawesome/pro-duotone-svg-icons/faCopyright';

import { useBctApiPermissions } from '@ngt/online-patient-management-bctapi-permissions';

/*
 * ---------------------------------------------------------------------------------
 * Imports - Internal
 * ---------------------------------------------------------------------------------
 */

 /**
  * Used to display the header of the application.
  */


import * as Dtos from './api/Neon.Dtos';

import PageLoading from './components/Loading/PageLoading';

import Registration from './containers/Registration';

import Preregistration from './containers/Forms/Preregistration';

import EligibilityChecklist from './containers/Forms/EligibilityChecklist';

import General from './containers/Forms/General';

import DiagnosticImagingAndTumourAssessment from './containers/Forms/DiagnosticImagingAndTumourAssessment';

import DiagnosticAndPathologyData from './containers/Forms/DiagnosticAndPathologyData';

import HaematologyAndBiochemistry from './containers/Forms/HaematologyAndBiochemistry';

import MedicalHistoryAndConcomitantMedications from './containers/Forms/MedicalHistoryAndConcomitantMedications';

import PatientSummary from './containers/PatientSummary';

import DrugManagementPage from './containers/DrugManagement/DrugManagementPage';

import BatchManagement from './containers/DrugManagement/BatchManagement';

import DepotManagement from './containers/DrugManagement/DepotManagement';

import AdministerDepot from './containers/DrugManagement/AdministerDepot';

import AdministerPharmacy from './containers/DrugManagement/AdministerPharmacy';

import DrugDispensations from './containers/DrugManagement/DrugDispensations';

import SaePage from './containers/Sae/SaePage';

import { Collapse, Toolbar } from '@material-ui/core';
import { Alert, AlertTitle } from '@material-ui/lab';
import { RequestState } from '@ngt/request-utilities';
import Errors from './Errors';
import HeaderV2, { IMenuItem } from './components/Header/HeaderV2';
import drugManagementSettings from './constants/drugManagementSettings';
import ErrorPage, { ErrorTypeEnum } from './containers/ErrorPage';
import Reports from './containers/Reports/Reports';
import SaeNotifyPage from './containers/Sae/SaeNotifyPage';
import SaeViewPage from './containers/Sae/SaeViewPage';

import { faEyeSlash, faFileChartLine, faFileMedical, faFirstAid, faNotesMedical, faUser } from '@fortawesome/pro-duotone-svg-icons';
import { AppSettings } from './AppSettings';
import bctLogo from './assets/img/bctlogo.png';

// const Registration = React.lazy(() => import('./containers/Registration'));

/*
 * ---------------------------------------------------------------------------------
 * Interfaces
 * ---------------------------------------------------------------------------------
 */

interface IAppProps {
    hasUser: boolean;
}

/*
 * ---------------------------------------------------------------------------------
 * Styles
 * ---------------------------------------------------------------------------------
 */

const useStyles = makeStyles<Theme>(theme => ({
    container: {
        marginBottom: theme.spacing(3)
    },
    footer: {
        padding: theme.spacing(3, 3),
        textAlign: 'center',
        color: theme.palette.primary.light,
    },
    banner: {
        borderRadius: 0,
        width: '100%'
    },
    bannerContainer: {
        position: 'fixed',
        top: '0px',
        width: '100%',
        zIndex: 10
    }
}))

/*
 * ---------------------------------------------------------------------------------
 * Constants
 * ---------------------------------------------------------------------------------
 */


const systemPermissions = [
    Dtos.Permission.OpmsPatientRegister,
    Dtos.Permission.DrugManagementView,
    Dtos.Permission.SaeView,
    Dtos.Permission.OpmsAdminister
];

/*
 * ---------------------------------------------------------------------------------
 * Components
 * ---------------------------------------------------------------------------------
 */

const App: React.FunctionComponent<IAppProps> = ({
    hasUser
}) => {

    const classes = useStyles();

    const [[canRegisterPatient, canViewDrugManagement, canViewSae, canAdministerOpms], permissionsLoadState] = useBctApiPermissions(systemPermissions);
    
    const [user, , ] = useAuthenticatedUser();

    const redirectRoute: JSX.Element = React.useMemo(() => {
        if (permissionsLoadState &&
            permissionsLoadState.state === RequestState.Success) {

            if (canRegisterPatient) {
                return <Redirect path="/" to="/registration" />;
            }
            else if (canViewDrugManagement) {
                return <Redirect path="/" to="/drug-management" />;
            }
            else if (canViewSae) {
                return <Redirect path="/" to="/sae" />;
            }
            else if (canAdministerOpms) {
                return <Redirect path="/" to="/reports" />;
            }
        }

        return <Redirect path="/" to="/error/403" />;
    },
    [
        canRegisterPatient,
        canViewSae,
        canViewDrugManagement,
        canAdministerOpms,
        permissionsLoadState
    ]);

    const [bannerOpen, setBannerOpen] = React.useState(false);

    React.useEffect(() => {
        const ua = window.navigator.userAgent;
        const msie = ua.indexOf('MSIE '); //IE 10 or older
        const trident = ua.indexOf('Trident/'); //IE 11

        if (msie > 0 || trident > 0) {
            // IE 11 or older, show banner
            setBannerOpen(true);
        }
    }, [setBannerOpen]);

    const validPermission = (permissionsLoadState.state === RequestState.Cancel || permissionsLoadState.state === RequestState.Failure || permissionsLoadState.state === RequestState.Success) && 
        canRegisterPatient ||
        canViewDrugManagement ||
        canViewSae ||
        canAdministerOpms

        const menuItems: IMenuItem[] = React.useMemo(() => {

            let items: IMenuItem[] = [];
    
            items.push(
                {
                    url: '/registration',
                    label: 'Registration',
                    external: false,
                    newTab: false,
                    icon: faUser as any
                }
            );
    
            if (canViewDrugManagement) {
                items.push({
                    url: '/drug-management',
                    label: 'Drug Management',
                    external: false,
                    newTab: false,
                    icon: faFirstAid as any
                });
            }
    
            if (canViewSae) {
                items.push({
                    url: '/sae',
                    label: 'SAE Review',
                    external: false,
                    newTab: false,
                    icon: faFileMedical as any
                });
            }
            
            if (canAdministerOpms) {
                items.push({
                    url: '/reports',
                    label: 'Reports',
                    external: false,
                    newTab: false,
                    icon: faFileChartLine as any
                });
            }

            items.push(
                {
                    url: AppSettings.ecrfUrl,
                    label: 'eCRF',
                    external: true,
                    newTab: true,
                    icon: faNotesMedical as any
                }
            );
    
            items.push(
                {
                    url: AppSettings.redactUrl,
                    label: 'Redact',
                    external: true,
                    newTab: true,
                    icon: faEyeSlash as any
                }
            );
    
            return items;
        }, [canViewSae, canViewDrugManagement, canAdministerOpms]);

    return (
        <>
            <Collapse in={bannerOpen} className={classes.bannerContainer}>
                <Alert severity="warning" className={classes.banner} square>
                    <AlertTitle>Your browser is not supported</AlertTitle>
                    Some features of this website may not work correctly with Internet Explorer. Please try using a different browser.
                </Alert>
            </Collapse>
            <CssBaseline />
            {/* <Header /> */}

            <HeaderV2 
                menuItems={menuItems} 
                organisationLogo={bctLogo}
                organisationLogoMobile={null}
                organisationName={"Breast Cancer Trials"}
                trialName={"Neo-N"}
                protocolId={"BCT 1902"}
                user={user}
                homePageUrl={AppSettings.trialPortalUrl}
                //applyDarkMode={applyDarkMode}
                
            />
            <Toolbar
            />
            

            {
                hasUser &&
                validPermission ?
                    <>
                        <React.Suspense
                            fallback={PageLoading}
                        >
                            <Switch>
                                <FormByContextRoute
                                    path={`/registration/:${onlinePatientManagement.routeParameters.countryCode}/:${onlinePatientManagement.routeParameters.institutionCode}/add-patient`}
                                    createNonExisting
                                    eventTypeCode="pre-registration"
                                    formTypeCode="pre-registration"
                                >
                                    <Preregistration />
                                </FormByContextRoute>
                                <FormByContextRoute
                                    path={[
                                        `/registration/:${onlinePatientManagement.routeParameters.countryCode}/:${onlinePatientManagement.routeParameters.institutionCode}/:${onlinePatientManagement.routeParameters.patientStudyNumber}/:${onlinePatientManagement.routeParameters.eventTypeCode}/:${onlinePatientManagement.routeParameters.eventRepeat}/medical-history-and-concomitant-medication/:${onlinePatientManagement.routeParameters.formRepeat}`,
                                        `/registration/:${onlinePatientManagement.routeParameters.countryCode}/:${onlinePatientManagement.routeParameters.institutionCode}/:${onlinePatientManagement.routeParameters.patientStudyNumber}/:${onlinePatientManagement.routeParameters.eventTypeCode}/:${onlinePatientManagement.routeParameters.eventRepeat}/medical-history-and-concomitant-medication`,
                                        `/registration/:${onlinePatientManagement.routeParameters.countryCode}/:${onlinePatientManagement.routeParameters.institutionCode}/:${onlinePatientManagement.routeParameters.patientStudyNumber}/:${onlinePatientManagement.routeParameters.eventTypeCode}/medical-history-and-concomitant-medication/:${onlinePatientManagement.routeParameters.formRepeat}`,
                                        `/registration/:${onlinePatientManagement.routeParameters.countryCode}/:${onlinePatientManagement.routeParameters.institutionCode}/:${onlinePatientManagement.routeParameters.patientStudyNumber}/:${onlinePatientManagement.routeParameters.eventTypeCode}/medical-history-and-concomitant-medication`
                                    ]}
                                    formTypeCode="medical-history-and-concomitant-medication"
                                    createNonExisting
                                    resolveBeforeLoad
                                >
                                    <MedicalHistoryAndConcomitantMedications />
                                </FormByContextRoute>
                                <FormByContextRoute
                                    path={[
                                        `/registration/:${onlinePatientManagement.routeParameters.countryCode}/:${onlinePatientManagement.routeParameters.institutionCode}/:${onlinePatientManagement.routeParameters.patientStudyNumber}/:${onlinePatientManagement.routeParameters.eventTypeCode}/:${onlinePatientManagement.routeParameters.eventRepeat}/haematology-and-biochemistry/:${onlinePatientManagement.routeParameters.formRepeat}`,
                                        `/registration/:${onlinePatientManagement.routeParameters.countryCode}/:${onlinePatientManagement.routeParameters.institutionCode}/:${onlinePatientManagement.routeParameters.patientStudyNumber}/:${onlinePatientManagement.routeParameters.eventTypeCode}/:${onlinePatientManagement.routeParameters.eventRepeat}/haematology-and-biochemistry`,
                                        `/registration/:${onlinePatientManagement.routeParameters.countryCode}/:${onlinePatientManagement.routeParameters.institutionCode}/:${onlinePatientManagement.routeParameters.patientStudyNumber}/:${onlinePatientManagement.routeParameters.eventTypeCode}/haematology-and-biochemistry/:${onlinePatientManagement.routeParameters.formRepeat}`,
                                        `/registration/:${onlinePatientManagement.routeParameters.countryCode}/:${onlinePatientManagement.routeParameters.institutionCode}/:${onlinePatientManagement.routeParameters.patientStudyNumber}/:${onlinePatientManagement.routeParameters.eventTypeCode}/haematology-and-biochemistry`
                                    ]}
                                    formTypeCode="haematology-and-biochemistry"
                                    createNonExisting
                                    resolveBeforeLoad
                                >
                                    <HaematologyAndBiochemistry />
                                </FormByContextRoute>
                                <FormByContextRoute
                                    path={[
                                        `/registration/:${onlinePatientManagement.routeParameters.countryCode}/:${onlinePatientManagement.routeParameters.institutionCode}/:${onlinePatientManagement.routeParameters.patientStudyNumber}/:${onlinePatientManagement.routeParameters.eventTypeCode}/:${onlinePatientManagement.routeParameters.eventRepeat}/diagnostic-and-pathology-data/:${onlinePatientManagement.routeParameters.formRepeat}`,
                                        `/registration/:${onlinePatientManagement.routeParameters.countryCode}/:${onlinePatientManagement.routeParameters.institutionCode}/:${onlinePatientManagement.routeParameters.patientStudyNumber}/:${onlinePatientManagement.routeParameters.eventTypeCode}/:${onlinePatientManagement.routeParameters.eventRepeat}/diagnostic-and-pathology-data`,
                                        `/registration/:${onlinePatientManagement.routeParameters.countryCode}/:${onlinePatientManagement.routeParameters.institutionCode}/:${onlinePatientManagement.routeParameters.patientStudyNumber}/:${onlinePatientManagement.routeParameters.eventTypeCode}/diagnostic-and-pathology-data/:${onlinePatientManagement.routeParameters.formRepeat}`,
                                        `/registration/:${onlinePatientManagement.routeParameters.countryCode}/:${onlinePatientManagement.routeParameters.institutionCode}/:${onlinePatientManagement.routeParameters.patientStudyNumber}/:${onlinePatientManagement.routeParameters.eventTypeCode}/diagnostic-and-pathology-data`
                                    ]}
                                    formTypeCode="diagnostic-and-pathology-data"
                                    createNonExisting
                                    resolveBeforeLoad
                                >
                                    <DiagnosticAndPathologyData />
                                </FormByContextRoute>
                                <FormByContextRoute
                                    path={[
                                        `/registration/:${onlinePatientManagement.routeParameters.countryCode}/:${onlinePatientManagement.routeParameters.institutionCode}/:${onlinePatientManagement.routeParameters.patientStudyNumber}/:${onlinePatientManagement.routeParameters.eventTypeCode}/:${onlinePatientManagement.routeParameters.eventRepeat}/diagnostic-imaging-and-tumour-assessment/:${onlinePatientManagement.routeParameters.formRepeat}`,
                                        `/registration/:${onlinePatientManagement.routeParameters.countryCode}/:${onlinePatientManagement.routeParameters.institutionCode}/:${onlinePatientManagement.routeParameters.patientStudyNumber}/:${onlinePatientManagement.routeParameters.eventTypeCode}/:${onlinePatientManagement.routeParameters.eventRepeat}/diagnostic-imaging-and-tumour-assessment`,
                                        `/registration/:${onlinePatientManagement.routeParameters.countryCode}/:${onlinePatientManagement.routeParameters.institutionCode}/:${onlinePatientManagement.routeParameters.patientStudyNumber}/:${onlinePatientManagement.routeParameters.eventTypeCode}/diagnostic-imaging-and-tumour-assessment/:${onlinePatientManagement.routeParameters.formRepeat}`,
                                        `/registration/:${onlinePatientManagement.routeParameters.countryCode}/:${onlinePatientManagement.routeParameters.institutionCode}/:${onlinePatientManagement.routeParameters.patientStudyNumber}/:${onlinePatientManagement.routeParameters.eventTypeCode}/diagnostic-imaging-and-tumour-assessment`
                                    ]}
                                    formTypeCode="diagnostic-imaging-and-tumour-assessment"
                                    createNonExisting
                                    resolveBeforeLoad
                                >
                                    <DiagnosticImagingAndTumourAssessment />
                                </FormByContextRoute>
                                <FormByContextRoute
                                    path={[
                                        `/registration/:${onlinePatientManagement.routeParameters.countryCode}/:${onlinePatientManagement.routeParameters.institutionCode}/:${onlinePatientManagement.routeParameters.patientStudyNumber}/:${onlinePatientManagement.routeParameters.eventTypeCode}/:${onlinePatientManagement.routeParameters.eventRepeat}/general/:${onlinePatientManagement.routeParameters.formRepeat}`,
                                        `/registration/:${onlinePatientManagement.routeParameters.countryCode}/:${onlinePatientManagement.routeParameters.institutionCode}/:${onlinePatientManagement.routeParameters.patientStudyNumber}/:${onlinePatientManagement.routeParameters.eventTypeCode}/:${onlinePatientManagement.routeParameters.eventRepeat}/general`,
                                        `/registration/:${onlinePatientManagement.routeParameters.countryCode}/:${onlinePatientManagement.routeParameters.institutionCode}/:${onlinePatientManagement.routeParameters.patientStudyNumber}/:${onlinePatientManagement.routeParameters.eventTypeCode}/general/:${onlinePatientManagement.routeParameters.formRepeat}`,
                                        `/registration/:${onlinePatientManagement.routeParameters.countryCode}/:${onlinePatientManagement.routeParameters.institutionCode}/:${onlinePatientManagement.routeParameters.patientStudyNumber}/:${onlinePatientManagement.routeParameters.eventTypeCode}/general`
                                    ]}
                                    formTypeCode="general"
                                    createNonExisting
                                    resolveBeforeLoad
                                >
                                    <General />
                                </FormByContextRoute>
                                <FormByContextRoute
                                    path={[
                                        `/registration/:${onlinePatientManagement.routeParameters.countryCode}/:${onlinePatientManagement.routeParameters.institutionCode}/:${onlinePatientManagement.routeParameters.patientStudyNumber}/:${onlinePatientManagement.routeParameters.eventTypeCode}/:${onlinePatientManagement.routeParameters.eventRepeat}/eligibility-checklist/:${onlinePatientManagement.routeParameters.formRepeat}`,
                                        `/registration/:${onlinePatientManagement.routeParameters.countryCode}/:${onlinePatientManagement.routeParameters.institutionCode}/:${onlinePatientManagement.routeParameters.patientStudyNumber}/:${onlinePatientManagement.routeParameters.eventTypeCode}/:${onlinePatientManagement.routeParameters.eventRepeat}/eligibility-checklist`,
                                        `/registration/:${onlinePatientManagement.routeParameters.countryCode}/:${onlinePatientManagement.routeParameters.institutionCode}/:${onlinePatientManagement.routeParameters.patientStudyNumber}/:${onlinePatientManagement.routeParameters.eventTypeCode}/eligibility-checklist/:${onlinePatientManagement.routeParameters.formRepeat}`,
                                        `/registration/:${onlinePatientManagement.routeParameters.countryCode}/:${onlinePatientManagement.routeParameters.institutionCode}/:${onlinePatientManagement.routeParameters.patientStudyNumber}/:${onlinePatientManagement.routeParameters.eventTypeCode}/eligibility-checklist`
                                    ]}
                                    formTypeCode="eligibility-checklist"
                                    createNonExisting
                                    resolveBeforeLoad
                                >
                                    <EligibilityChecklist />
                                </FormByContextRoute>
                                <FormByContextRoute
                                    path={[
                                        `/registration/:${onlinePatientManagement.routeParameters.countryCode}/:${onlinePatientManagement.routeParameters.institutionCode}/add-patient`,
                                        `/registration/:${onlinePatientManagement.routeParameters.countryCode}/:${onlinePatientManagement.routeParameters.institutionCode}/:${onlinePatientManagement.routeParameters.patientStudyNumber}/:${onlinePatientManagement.routeParameters.eventTypeCode}/:${onlinePatientManagement.routeParameters.eventRepeat}/pre-registration/:${onlinePatientManagement.routeParameters.formRepeat}`,
                                        `/registration/:${onlinePatientManagement.routeParameters.countryCode}/:${onlinePatientManagement.routeParameters.institutionCode}/:${onlinePatientManagement.routeParameters.patientStudyNumber}/:${onlinePatientManagement.routeParameters.eventTypeCode}/:${onlinePatientManagement.routeParameters.eventRepeat}/pre-registration`,
                                        `/registration/:${onlinePatientManagement.routeParameters.countryCode}/:${onlinePatientManagement.routeParameters.institutionCode}/:${onlinePatientManagement.routeParameters.patientStudyNumber}/:${onlinePatientManagement.routeParameters.eventTypeCode}/pre-registration/:${onlinePatientManagement.routeParameters.formRepeat}`,
                                        `/registration/:${onlinePatientManagement.routeParameters.countryCode}/:${onlinePatientManagement.routeParameters.institutionCode}/:${onlinePatientManagement.routeParameters.patientStudyNumber}/:${onlinePatientManagement.routeParameters.eventTypeCode}/pre-registration`
                                    ]}
                                    formTypeCode="pre-registration"
                                    createNonExisting
                                    resolveBeforeLoad
                                >
                                    <Preregistration />
                                </FormByContextRoute>
                                <PatientByStudyNumberRoute
                                    path={`/registration/:${onlinePatientManagement.routeParameters.countryCode}/:${onlinePatientManagement.routeParameters.institutionCode}/:${onlinePatientManagement.routeParameters.patientStudyNumber}`}
                                >
                                    <PatientSummary />
                                </PatientByStudyNumberRoute>
                                <InstitutionsRoute
                                    path={[
                                        `/registration/:${onlinePatientManagement.routeParameters.countryCode}/:${onlinePatientManagement.routeParameters.institutionCode}`,
                                        `/registration/:${onlinePatientManagement.routeParameters.countryCode}`,
                                        '/registration'
                                    ]}
                                    resolveBeforeLoad
                                >
                                    <Registration />
                                </InstitutionsRoute>
                                <Route
                                    path={[
                                        `/drug-management/batches`
                                    ]}
                                >
                                    <BatchManagement />
                                </Route>
                                <Route
                                    path={[
                                        `/drug-management/depots`
                                    ]}
                                >
                                    <DepotManagement />
                                </Route>
                                <Route
                                    path={[
                                        `/drug-management/pharmacy/:pharmacyId`
                                    ]}
                                >
                                    <AdministerPharmacy />
                                </Route>
                                <Route
                                    path={[
                                        `/drug-management/depot/:depotId`
                                    ]}>
                                    <AdministerDepot />
                                </Route>
                                <PatientByStudyNumberRoute
                                    path={[
                                        `/drug-management/:${onlinePatientManagement.routeParameters.institutionCode}/:${onlinePatientManagement.routeParameters.patientStudyNumber}/drug-dispensation`
                                    ]}
                                >
                                    <DrugDispensations />
                                </PatientByStudyNumberRoute>
                                <InstitutionsRoute
                                    path={[
                                        `/drug-management/:${onlinePatientManagement.routeParameters.institutionCode}`,
                                        `/drug-management`
                                    ]}
                                    resolveBeforeLoad
                                >
                                    {
                                        drugManagementSettings.show ?
                                            <DrugManagementPage /> :
                                            <Redirect to="/error/404" />
                                    }
                                </InstitutionsRoute>
                                <InstitutionByCodeRoute
                                    path={[
                                        `/sae/:${onlinePatientManagement.routeParameters.institutionCode}/:${onlinePatientManagement.routeParameters.patientStudyNumber}/:saeNumber/notify/:notifyMode`,
                                    ]}
                                    resolveBeforeLoad
                                >
                                    <SaeNotifyPage />
                                </InstitutionByCodeRoute>
                                <InstitutionByCodeRoute
                                    path={[
                                        `/sae/:${onlinePatientManagement.routeParameters.institutionCode}/:${onlinePatientManagement.routeParameters.patientStudyNumber}/:saeNumber/:viewMode`,
                                        `/sae/:${onlinePatientManagement.routeParameters.institutionCode}/:${onlinePatientManagement.routeParameters.patientStudyNumber}/:saeNumber`,
                                    ]}
                                    resolveBeforeLoad
                                >
                                    <SaeViewPage />
                                </InstitutionByCodeRoute>
                                <InstitutionsRoute
                                    path={[
                                        `/sae/:${onlinePatientManagement.routeParameters.institutionCode}`,
                                        `/sae`
                                    ]}
                                    resolveBeforeLoad
                                >
                                    <SaePage />
                                </InstitutionsRoute>
                                <InstitutionsRoute
                                    path={[
                                        `/reports/:${onlinePatientManagement.routeParameters.institutionCode}`,
                                        `/reports`
                                    ]}
                                    resolveBeforeLoad
                                >
                                    <Reports />
                                </InstitutionsRoute>
                                <Route path={
                                    [
                                        '/error/:errorCode',
                                        '/error'
                                    ]}
                                >
                                    <Errors />
                                </Route>
                                {
                                    redirectRoute
                                }
                            </Switch>
                        </React.Suspense>
                    </> :
                !hasUser ||
                permissionsLoadState &&
                ((permissionsLoadState.state === RequestState.Pending ||
                permissionsLoadState.state === RequestState.None)) ?
                    <RouteLoading /> :    
                    <ErrorPage errorType={ErrorTypeEnum.e403} />
            }
            <div className={classes.footer}>
                <Typography variant="caption">
                    Copyright <FontAwesomeIcon icon={faCopyright} fixedWidth/> 2025 ANZ Breast Cancer Trials Group Ltd | ABN 64 051 369 496
                </Typography>
            </div>
        </>
    );
};

/*
 * ---------------------------------------------------------------------------------
 * Default Export
 * ---------------------------------------------------------------------------------
 */
export default App;